import {useLayoutEffect,useRef,useState,type ReactNode} from 'react';
import type {TedIntonationSpan} from '@/types/screenplay';
import {performanceAttributes} from '@/utils/performanceAttributes';
import {useModifierDisplay} from '@/contexts/ModifierDisplay';

type TextBox={left:number;top:number;width:number;height:number};
type PassageBox={id:string;rects:TextBox[];font:number};

/** Presentation only. The native textarea remains the exact authored string.
 * Hover hit-testing uses its identically laid-out text mirror, not overlay hits. */
export default function PerformanceSegmentLabels({text,spans,children,onInspect,selection}:{
  text:string;spans:TedIntonationSpan[];children?:ReactNode;onInspect?:(span:TedIntonationSpan|null)=>void;
  selection?:{start:number;end:number};
}){
  const {mode,color}=useModifierDisplay();
  const root=useRef<HTMLDivElement>(null);
  const [passages,setPassages]=useState<PassageBox[]>([]);
  const [selectionRects,setSelectionRects]=useState<TextBox[]>([]);
  const lastHover=useRef<string|null|undefined>(undefined);
  const annotated=spans.filter(s=>s.start>=0&&s.end<=text.length&&s.end>s.start&&
    s.selectedText===text.slice(s.start,s.end)&&s.selectedText.trim()&&performanceAttributes(s).length);
  const byId=new Map(annotated.map(span=>[span.id,span]));
  useLayoutEffect(()=>{
    const container=root.current;if(!container)return;
    let cancelled=false;
    const measure=()=>{
      if(cancelled)return;
      const bounds=container.getBoundingClientRect(),next:PassageBox[]=[];
      const ids=new Set(annotated.map(s=>s.id));
      for(const node of container.querySelectorAll<HTMLElement>('[data-delivery-range]')){
        const id=node.dataset.deliveryRange!;if(!ids.has(id))continue;
        const rects:TextBox[]=[];
        const range=document.createRange(),content=node.textContent??'';
        const trimStart=content.length-content.trimStart().length,trimEnd=content.trimEnd().length;
        let layoutRects=node.getClientRects();
        if(node.firstChild?.nodeType===Node.TEXT_NODE&&typeof range.getClientRects==='function'){
          range.setStart(node.firstChild,trimStart);range.setEnd(node.firstChild,trimEnd);layoutRects=range.getClientRects();
        }
        for(const rect of layoutRects){
          const left=Math.max(0,rect.left-bounds.left),width=Math.min(rect.width,bounds.width-left);
          if(width>0&&rect.height>0)rects.push({left,top:rect.top-bounds.top,width,height:rect.height});
        }
        if(rects.length)next.push({id,rects,font:parseFloat(getComputedStyle(node).fontSize)||14});
      }
      setPassages(previous=>JSON.stringify(previous)===JSON.stringify(next)?previous:next);
      const selected:TextBox[]=[];
      const input=container.querySelector('textarea');
      if(input&&selection&&Number.isInteger(selection.start)&&Number.isInteger(selection.end)&&
        selection.start>=0&&selection.end<=text.length&&selection.end>selection.start){
        // A temporary, hidden mirror measures native wrapping even before the
        // passage has saved attributes. It never changes the authored textarea.
        const mirror=document.createElement('div'),style=getComputedStyle(input),box=input.getBoundingClientRect();
        mirror.setAttribute('aria-hidden','true');
        Object.assign(mirror.style,{position:'absolute',visibility:'hidden',pointerEvents:'none',boxSizing:'border-box',
          left:`${box.left-bounds.left}px`,top:`${box.top-bounds.top-input.scrollTop}px`,width:`${box.width}px`,
          whiteSpace:'pre-wrap',overflowWrap:'break-word',border:'1px solid transparent'});
        for(const key of ['fontFamily','fontSize','fontWeight','fontStyle','fontVariant','fontKerning','lineHeight','letterSpacing','wordSpacing','textAlign','textTransform','textIndent','direction','tabSize','paddingTop','paddingRight','paddingBottom','paddingLeft','borderTopWidth','borderRightWidth','borderBottomWidth','borderLeftWidth'] as const)mirror.style[key]=style[key];
        const node=document.createTextNode(text);mirror.append(node);container.append(mirror);
        try{
          const range=document.createRange();range.setStart(node,selection.start);range.setEnd(node,selection.end);
          if(typeof range.getClientRects==='function')for(const rect of range.getClientRects()){
            const left=Math.max(0,rect.left-bounds.left),width=Math.min(rect.width,bounds.width-left);
            if(width>0&&rect.height>0)selected.push({left,top:rect.top-bounds.top,width,height:rect.height});
          }
        }finally{mirror.remove();}
      }
      setSelectionRects(previous=>JSON.stringify(previous)===JSON.stringify(selected)?previous:selected);
    };
    measure();const observer=typeof ResizeObserver==='undefined'?null:new ResizeObserver(measure);
    observer?.observe(container);
    for(const node of container.querySelectorAll('textarea,[data-performance-text-layer]'))observer?.observe(node);
    window.addEventListener('resize',measure);void document.fonts?.ready.then(measure);
    return()=>{cancelled=true;observer?.disconnect();window.removeEventListener('resize',measure);};
  },[text,spans,selection?.start,selection?.end]);
  return <div ref={root} data-performance-range-root className={`relative isolate${annotated.length?' script-attributed-text':''}`}
    onPointerLeave={()=>{lastHover.current=undefined;}}
    onPointerMoveCapture={event=>{
      if(event.buttons)return;
      if((event.target as Element).closest('[data-performance-bracket]'))return;
      const bounds=event.currentTarget.getBoundingClientRect(),x=event.clientX-bounds.left,y=event.clientY-bounds.top;
      const passage=passages.find(p=>p.rects.some(r=>x>=r.left&&x<=r.left+r.width&&y>=r.top&&y<=r.top+r.height));
      const id=passage?.id??null;
      if(id!==lastHover.current){lastHover.current=id;onInspect?.(id?byId.get(id)??null:null);}
    }}>
    {children}
    {selectionRects.length>0&&<div className="script-retained-selection" aria-hidden="true" data-selection-start={selection?.start} data-selection-end={selection?.end}>
      {selectionRects.map((rect,index)=><span key={index} style={{left:rect.left,top:rect.top,width:rect.width,height:rect.height}}/>)}
    </div>}
    {mode==='highlighted'&&<div className="script-modifier-highlights" aria-hidden="true">
      {passages.flatMap(passage=>passage.rects.map((rect,index)=><span key={`${passage.id}:${index}`} data-modifier-highlight={passage.id}
        style={{left:rect.left,top:rect.top,width:rect.width,height:rect.height,backgroundColor:`${color}40`}}/>))}
    </div>}
    {mode==='bracketed'&&<div className="script-performance-markers" aria-label="Passage attribute markers">
      {passages.flatMap(passage=>{
        const span=byId.get(passage.id);if(!span)return [];
        const attributes=performanceAttributes(span).map(a=>`${a.type}: ${a.value}`).join(' · ');
        return (['start','end'] as const).map(edge=>{
          const rect=edge==='start'?passage.rects[0]:passage.rects.at(-1)!;
          return <button key={`${passage.id}:${edge}`} type="button" className="script-performance-bracket"
            data-performance-bracket={passage.id} data-bracket-edge={edge}
            aria-label={`${edge==='start'?'Start':'End'} of attributed passage. ${attributes}`}
            style={{top:Math.max(0,rect.top-passage.font*.12),left:edge==='start'?rect.left:rect.left+rect.width,
              fontSize:passage.font*1.2,transform:edge==='start'?'translateX(-100%)':undefined}}
            onPointerEnter={()=>onInspect?.(span)} onFocus={()=>onInspect?.(span)}
            onPointerDown={event=>event.preventDefault()}
            onClick={event=>{event.stopPropagation();onInspect?.(span);}}
            onKeyDown={event=>{if(event.key==='Escape'){event.stopPropagation();onInspect?.(null);lastHover.current=undefined;}}}>
            {edge==='start'?'[':']'}
          </button>;
        });
      })}
    </div>}
  </div>;
}
